/* ============================================================
   Sudsy Paws — support widget (TypeScript source -> /support.js)
   Channels: in-browser voice (Retell Web SDK), callback, SMS, tel.
   The Retell SDK is imported lazily (dynamic import) only when the
   user starts a voice call, so a CDN hiccup never breaks the panel,
   tel/SMS links, or the callback flow.
   ============================================================ */
const RETELL_SDK_URL = 'https://esm.sh/retell-client-js-sdk@2';
// Backend base URL injected by Liquid (the externally-hosted Azure Functions
// app) via window.__SUDSY__.apiBase. Empty string -> same-origin relative
// paths (legacy / local dev). Read loosely to avoid coupling to app.ts types.
const API_BASE = (window.__SUDSY__ && window.__SUDSY__.apiBase) || '';
/* ---- config ---- */
const SUPPORT = {
    // The Retell phone number customers text / call (E.164).
    phoneNumber: '+12364626188',
    // Pre-filled SMS body when they tap "Text us".
    smsBody: 'Hi Sudsy Paws — I have a question about',
};
const $ = (sel) => document.querySelector(sel);
const fab = $('[data-support-open]');
const panel = $('[data-support-panel]');
const closeBtn = $('[data-support-close]');
const statusEl = $('[data-support-status]');
const talkBtn = $('[data-support-talk]');
const endBtn = $('[data-support-end]');
const numberEl = $('[data-support-number]');
const callMeBtn = $('[data-support-callme]');
const smsLink = $('[data-support-sms]');
const telLink = $('[data-support-tel]');
// RetellWebClient instance (typed loosely — SDK is loaded at runtime from a CDN).
let client = null;
let inCall = false;
const supportWidgetReady = Boolean(fab && panel && closeBtn && talkBtn && endBtn && numberEl && callMeBtn);
if (supportWidgetReady) {
/* ---- tel:/sms: links (no backend needed) ---- */
(function initLinks() {
    const num = SUPPORT.phoneNumber;
    if (telLink)
        telLink.href = 'tel:' + num;
    if (smsLink)
        smsLink.href = 'sms:' + num + '?&body=' + encodeURIComponent(SUPPORT.smsBody);
})();
/* ---- open / close panel ---- */
function openPanel() {
    panel.hidden = false;
    fab.setAttribute('aria-expanded', 'true');
}
function closePanel() {
    panel.hidden = true;
    fab.setAttribute('aria-expanded', 'false');
}
fab.addEventListener('click', () => (panel.hidden ? openPanel() : closePanel()));
closeBtn.addEventListener('click', closePanel);
document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !panel.hidden)
        closePanel();
});
function setStatus(text) {
    if (statusEl)
        statusEl.textContent = text;
}
/* ---- in-browser voice call ---- */
async function startTalk() {
    if (inCall)
        return;
    setStatus('Connecting…');
    talkBtn.disabled = true;
    try {
        const res = await fetch(API_BASE + '/api/retell/web-call', { method: 'POST' });
        if (!res.ok)
            throw new Error('token request failed');
        const { accessToken } = (await res.json());
        if (!accessToken)
            throw new Error('no token');
        const mod = await import(RETELL_SDK_URL);
        const RetellWebClient = mod.RetellWebClient;
        client = new RetellWebClient();
        client.on('call_started', () => {
            inCall = true;
            talkBtn.hidden = true;
            endBtn.hidden = false;
            talkBtn.disabled = false;
            setStatus('🔴 Live — go ahead and talk');
        });
        client.on('call_ended', endCallUI);
        client.on('error', (err) => {
            console.error('Retell error', err);
            endCallUI();
            setStatus('Call ended. Tap to try again.');
        });
        await client.startCall({ accessToken });
    }
    catch (err) {
        console.error('startTalk failed', err);
        talkBtn.disabled = false;
        setStatus('Could not start the call. Try “Call me back”, text, or call us below.');
    }
}
function endTalk() {
    try {
        if (client)
            client.stopCall();
    }
    catch {
        /* ignore */
    }
    endCallUI();
}
function endCallUI() {
    inCall = false;
    client = null;
    endBtn.hidden = true;
    talkBtn.hidden = false;
    talkBtn.disabled = false;
    setStatus('Ask about orders, sizes, shipping & more');
}
talkBtn.addEventListener('click', startTalk);
endBtn.addEventListener('click', endTalk);
/* ---- callback (AI calls the customer) ---- */
async function requestCallback() {
    const toNumber = (numberEl.value || '').trim();
    if (!/^\+?[0-9\s()-]{8,}$/.test(toNumber)) {
        setStatus('Enter your number in international format, e.g. +1 415 555 1234.');
        return;
    }
    callMeBtn.disabled = true;
    setStatus('Calling you now…');
    try {
        const res = await fetch(API_BASE + '/api/retell/phone-call', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ toNumber: toNumber.replace(/[\s()-]/g, '') }),
        });
        const data = (await res.json().catch(() => ({})));
        if (!res.ok)
            throw new Error(data.error || 'failed');
        setStatus('📞 Calling you now — please pick up!');
    }
    catch (err) {
        const msg = err.message;
        setStatus(typeof msg === 'string' && msg.includes('+') ? msg : 'Could not place the call. Please check your number and try again.');
    }
    finally {
        callMeBtn.disabled = false;
    }
}
callMeBtn.addEventListener('click', requestCallback);
}
export {};
